import React, { CSSProperties, useMemo } from 'react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { AddBoxOutlined, IndeterminateCheckBoxOutlined } from '@material-ui/icons';
import { TreeItem, TreeView } from '@material-ui/lab';
import * as _ from 'lodash';
import {
  DOCUMENT_TYPES_BIDDING,
  DOCUMENT_TYPES_CLOSE_OUT,
  DOCUMENT_TYPES_CONSTRUCTION,
  DOCUMENT_TYPES_DESIGN,
  getUserFriendlyDocumentTemplateNameSingular,
} from '../../scripts/utils';
import LabelWithCheckbox from './LabelWithCheckbox';
import { DocumentTemplateType } from '../../api-client/autogenerated';

/**
 * The file manager is not a document template, but it is selected alongside them in the Design
 * phase, so it travels through the selection as a plain string.
 */
export const FILE_MANAGER_DOCUMENT_TYPE = 'File Manager';

export const DOCUMENT_TYPE_PHASES: [string, (DocumentTemplateType | string)[]][] = [
  ['Design', [FILE_MANAGER_DOCUMENT_TYPE, ...DOCUMENT_TYPES_DESIGN]],
  ['Bidding', DOCUMENT_TYPES_BIDDING],
  ['Construction', DOCUMENT_TYPES_CONSTRUCTION],
  ['Close Out', DOCUMENT_TYPES_CLOSE_OUT],
];

export const ALL_DOCUMENT_TYPES: (DocumentTemplateType | string)[] = DOCUMENT_TYPE_PHASES.flatMap(
  ([, types]) => types,
);

const useStyles = makeStyles(() =>
  createStyles({
    treeItemRoot: {
      '&:focus > .MuiTreeItem-content .MuiTreeItem-label': {
        backgroundColor: 'transparent',
      },
      '&.Mui-selected > .MuiTreeItem-content .MuiTreeItem-label': {
        backgroundColor: 'transparent',
      },
      '&.Mui-selected > .MuiTreeItem-content .MuiTreeItem-label:hover, &.Mui-selected:focus > .MuiTreeItem-content .MuiTreeItem-label': {
        backgroundColor: 'transparent',
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    treeItemLabel: {
      whiteSpace: 'nowrap',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    treeItemGroup: {
      marginTop: 4,
    },
    parentTreeItemLabel: {
      paddingLeft: 0,
    },
  }),
);

export type DocumentTypeTreeViewProps = {
  selectedDocumentTypes: (DocumentTemplateType | string)[];
  setSelectedDocumentTypes: React.Dispatch<React.SetStateAction<(DocumentTemplateType | string)[]>>;
  /** Emails are only offered when a setter is provided, since they are not a document type. */
  includeEmails?: boolean;
  setIncludeEmails?: (includeEmails: boolean) => void;
  /**
   * Types to leave out of the tree entirely. Pass a stable reference -- a fresh array literal on
   * every render defeats the memo below.
   */
  hiddenDocumentTypes?: (DocumentTemplateType | string)[];
  /** Shows a selection without letting it be changed, for looking at a past export. */
  disabled?: boolean;
  style?: CSSProperties;
};

/**
 * The document types a project has, grouped by project phase. Shared by the search filters and the
 * project export dialog so both offer the same list.
 */
export default function DocumentTypeTreeView(props: DocumentTypeTreeViewProps) {
  const {
    selectedDocumentTypes,
    setSelectedDocumentTypes,
    includeEmails,
    setIncludeEmails,
    hiddenDocumentTypes,
    disabled,
    style,
  } = props;
  const classes = useStyles();

  const phases = useMemo(() => {
    if (!hiddenDocumentTypes?.length) return DOCUMENT_TYPE_PHASES;
    return DOCUMENT_TYPE_PHASES.map(
      ([label, types]) =>
        [label, types.filter((type) => !hiddenDocumentTypes.includes(type))] as [
          string,
          (DocumentTemplateType | string)[],
        ],
    ).filter(([, types]) => types.length > 0);
  }, [hiddenDocumentTypes]);

  return (
    <TreeView
      defaultCollapseIcon={<IndeterminateCheckBoxOutlined />}
      defaultExpandIcon={<AddBoxOutlined />}
      style={{ padding: 6, border: '1px solid #949494', borderRadius: '4px', ...style }}
    >
      {setIncludeEmails && (
        <TreeItem
          nodeId="Emails"
          label={
            <LabelWithCheckbox
              label="Emails"
              checked={includeEmails}
              disabled={disabled}
              onChange={(_event, checked) => setIncludeEmails(checked)}
            />
          }
          classes={{ label: classes.parentTreeItemLabel }}
          style={{ marginTop: 2, marginBottom: 2 }}
        >
          <TreeItem
            nodeId="Correspondence Log"
            label={
              <LabelWithCheckbox
                label="Correspondence Log"
                checked={includeEmails}
                disabled={disabled}
                onChange={(_event, checked) => setIncludeEmails(checked)}
              />
            }
            classes={{
              root: classes.treeItemRoot,
              label: classes.treeItemLabel,
              group: classes.treeItemGroup,
            }}
            style={{ marginTop: 2, marginBottom: 2 }}
          />
        </TreeItem>
      )}
      {phases.map(([label, types]) => (
        <TreeItem
          key={label}
          nodeId={label}
          label={
            <LabelWithCheckbox
              label={label}
              checked={types.every((type) => selectedDocumentTypes.includes(type))}
              indeterminate={
                types.some((t) => selectedDocumentTypes.includes(t)) &&
                !types.every((t) => selectedDocumentTypes.includes(t))
              }
              disabled={disabled}
              onChange={(_event, checked) =>
                checked
                  ? setSelectedDocumentTypes((prev) => _.uniq([...prev, ...types]))
                  : setSelectedDocumentTypes((prev) => prev.filter((type) => !types.includes(type)))
              }
            />
          }
          classes={{ label: classes.parentTreeItemLabel }}
          style={{ marginTop: 2, marginBottom: 2 }}
        >
          {types.map((type) => (
            <TreeItem
              key={type}
              nodeId={type}
              label={
                <LabelWithCheckbox
                  label={
                    typeof type === 'string'
                      ? type
                      : getUserFriendlyDocumentTemplateNameSingular(type)
                  }
                  checked={selectedDocumentTypes.includes(type)}
                  disabled={disabled}
                  onChange={(_event, checked) =>
                    checked
                      ? setSelectedDocumentTypes((prev) => [...prev, type])
                      : setSelectedDocumentTypes((prev) => prev.filter((t) => t !== type))
                  }
                />
              }
              classes={{
                root: classes.treeItemRoot,
                label: classes.treeItemLabel,
                group: classes.treeItemGroup,
              }}
              style={{ marginTop: 2, marginBottom: 2 }}
            />
          ))}
        </TreeItem>
      ))}
    </TreeView>
  );
}
